import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { protectionOperations } from './nuvora-shipping-protection-plan.js';

const variantId = 48171113906337;
const root = window.Shopify?.routes?.root || '/';
const ownEvents = new WeakSet();
const disabledCheckoutStates = new WeakMap();
let queue = Promise.resolve();
let pending = 0;

function controls() {
  return document.querySelectorAll('[data-nuvora-shipping-protection]');
}

function setBusy(busy) {
  for (const widget of controls()) {
    widget.setAttribute('aria-busy', String(busy));
    const checkbox = widget.querySelector('input[type="checkbox"]');
    if (checkbox) checkbox.disabled = busy;
  }
  for (const button of document.querySelectorAll('button[name="checkout"]')) {
    if (busy) {
      if (!disabledCheckoutStates.has(button)) disabledCheckoutStates.set(button, button.disabled);
      button.disabled = true;
    } else if (disabledCheckoutStates.has(button)) {
      button.disabled = disabledCheckoutStates.get(button);
      disabledCheckoutStates.delete(button);
    }
  }
}

function sync(cart) {
  const hasProducts = cart.items.some((item) => String(item.variant_id) !== String(variantId) && item.quantity > 0);
  const checked = hasProducts && cart.items.some((item) => String(item.variant_id) === String(variantId));
  for (const widget of controls()) {
    const label = widget.querySelector('[data-nuvora-protection-label]');
    const checkbox = widget.querySelector('input[type="checkbox"]');
    if (label) label.hidden = !hasProducts;
    if (checkbox) {
      checkbox.checked = checked;
      checkbox.disabled = pending > 0 || !hasProducts;
    }
  }
}

async function readCart() {
  const response = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new Error('Could not read cart');
  return response.json();
}

async function mutate(operation) {
  const sections = Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'),
    (component) => component.dataset.sectionId)));
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action: operation.quantity === 0 ? 'remove' : 'update',
    context: 'cart',
    lines: [{ id: String(operation.id), quantity: operation.quantity }],
    promise: deferred.promise,
  });
  ownEvents.add(event);
  document.dispatchEvent(event);
  try {
    const body = operation.kind === 'add'
      ? { items: [{ id: variantId, quantity: 1 }] }
      : { id: operation.id, quantity: operation.quantity };
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname + window.location.search;
    const response = await fetch(`${root}cart/${operation.kind === 'add' ? 'add' : 'change'}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || 'Could not update Shipping Protection');
    const cart = operation.kind === 'change' ? result : await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: result.sections, items: cart.items, itemCount: cart.item_count,
        source: 'nuvora-shipping-protection', didError: false },
    });
    await Promise.all(Array.from(document.querySelectorAll('cart-items-component'),
      (component) => component.cartUpdateComplete));
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

async function reconcile(enabled = null) {
  await Promise.all(CartItemsComponent.pendingQuantityUpdates);
  let cart = await readCart();
  for (let attempt = 0; attempt < 20; attempt++) {
    const operation = protectionOperations(cart, variantId, enabled)[0];
    if (!operation) {
      sync(cart);
      return;
    }
    cart = await mutate(operation);
  }
  throw new Error('Could not reconcile Shipping Protection');
}

function clearErrors() {
  for (const widget of controls()) {
    const error = widget.querySelector('[data-nuvora-protection-error]');
    if (error) error.hidden = true;
  }
}

function enqueue(enabled = null, showError = false) {
  pending++;
  setBusy(true);
  queue = queue.then(() => reconcile(enabled)).catch(async (error) => {
    console.warn('[nuvora-shipping-protection]', error);
    if (showError) {
      for (const widget of controls()) {
        const message = widget.querySelector('[data-nuvora-protection-error]');
        if (message) message.hidden = false;
      }
    }
    try { sync(await readCart()); } catch {}
  }).finally(() => {
    pending--;
    setBusy(pending > 0);
  });
  return queue;
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-nuvora-protection-input]')) return;
  clearErrors();
  enqueue(input.checked, true);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (ownEvents.has(event)) return;
  event.promise?.then(() => enqueue()).catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) enqueue();
});

if (document.querySelector('[data-nuvora-protection-input]:checked, .cart-items__table-row--nuvora-protection')) enqueue();
